import React, { useState } from 'react';
import { compose } from 'redux';
import classNames from 'classnames';

import { FormattedMessage, injectIntl } from '../../../util/reactIntl';
import {
    AnonymCarouselPlaceholder,
    Button,
    IconSpinner,
    ModalPortal,
    NamedLink,
} from '../../../components';
import { ThumbnailWrapper } from '../../../components/AddImages/ThumbnailWrapper';

import GalleryIcon from '../icons/GalleryIcon';
import { USER_IMAGES_VIDEOS } from '../config';
import { GALLERY_FORM } from '../../../containers/EditUserInfoPage/EditUserInfoPage';
import { GALLERY_ID } from '../NavigationBar';
import css from '../ProfileSettingsForm.css';
import sectionCss from './GallerySection.css';
import { useGalleryImages } from '../../../hooks/useGalleryImages';
import { useIsMobile } from '../../../hooks/useIsMobile';

const GallerySection = ({
    renderEditPencilIcon,
    uploadDocumentsInProgress,
    userDocuments,
    rootClassName,
    className,
    isPublic,
    userType,
    isLoggedOut,
    displayName,
    isAnonym,
    acceptInquirySection,
}) => {
    const [isMobile] = useIsMobile();
    const [galleryImages] = useGalleryImages({
        uploadDocumentsInProgress,
        userDocuments,
    });
    const [imageCarouselOpen, setImageCarouselOpen] = useState(false);

    const noImages = galleryImages.length === 0;

    const rootClasses = classNames(css.infoBlock, {
        [css.noInfoBlock]: !!noImages,
    });

    /** another logged in user visits the page with no gallery */
    if (isPublic && noImages && !isLoggedOut) {
        return (
            <div className={css.infoBlock}>
                <h2 className={css.infoHeading}>
                    <span>Galerie</span>
                </h2>
                <p className={css.noData}>
                    {displayName} hat noch keine Fotos und Videos hinzugefügt.
                </p>
            </div>
        );
    }
    /** another logged out user visits the page with no gallery */
    if (isPublic && noImages) return null;

    const classes = classNames(rootClassName || css.root, className);

    const [first, second, third, fourth, ...restImages] = galleryImages || [];

    const renderCarouselImageElem = ({ imageElem, videoElem, image }) => (
        <AnonymCarouselPlaceholder
            isAnonymousListing={!image.isDefaultAvatar && isAnonym}
            assetElem={imageElem || videoElem}
            type="profile"
        >
            {acceptInquirySection}
        </AnonymCarouselPlaceholder>
    );

    const commonProps = {
        onDragOver: () => null,
        onDragStart: () => null,
        onDragEnd: () => null,
        onDrop: () => null,
        onDragLeave: () => null,
        onRemoveImage: () => null,
        savedImageAltText: '',
        hiddenAsset: isAnonym,
        renderCarouselImageElem,
        renderCarouselVideoElem: renderCarouselImageElem,
        wrapperClassName: sectionCss.threeToTwoWrapper,
    };

    const firstImage = first ? (
        <ThumbnailWrapper
            image={first}
            index={0}
            key={first.id?.uuid || first.id}
            allImages={galleryImages || []}
            showImageCarousel
            {...commonProps}
            wrapperClassName={classNames(commonProps.wrapperClassName, sectionCss.firstImage)}
        />
    ) : null;

    const secondColumnImages = [second, third, fourth].map((image, index) =>
        image ? (
            <ThumbnailWrapper
                image={image}
                index={index + 1}
                key={image.id?.uuid || image.id}
                allImages={galleryImages || []}
                showImageCarousel
                showCarouselClassName={sectionCss.showCarouselClassName}
                {...commonProps}
                wrapperClassName={classNames(commonProps.wrapperClassName, sectionCss.secondImage)}
                isMobile={isMobile}
            />
        ) : null
    );

    const restImagesColumn = restImages.map((image, index) =>
        image ? (
            <ThumbnailWrapper
                image={image}
                index={index + 4}
                key={image.id?.uuid || image.id}
                allImages={galleryImages || []}
                showCarouselClassName={sectionCss.showCarouselClassName}
                showImageCarousel
                {...commonProps}
                isMobile={isMobile}
            />
        ) : null
    );

    const publicContent = isPublic && (
        <code className={css.pntr} onClick={() => setImageCarouselOpen(true)}>
            <GalleryIcon />
        </code>
    );

    const privateContent = !isPublic && !noImages && (
        <NamedLink
            name="EditUserInfo"
            params={{ section: GALLERY_FORM, userType: userType || 'rider' }}
            className={sectionCss.link}
        >
            <div className={css.editBlock}>
                {renderEditPencilIcon(
                    USER_IMAGES_VIDEOS,
                    null,
                    <p className={css.editText}>
                        <FormattedMessage id="ProfileSettingsForm.editText" />
                    </p>
                )}
            </div>
        </NamedLink>
    );

    if (uploadDocumentsInProgress) {
        return <IconSpinner />;
    }

    return (
        <>
            <div className={rootClasses} id={GALLERY_ID}>
                <h2 className={css.infoHeading}>
                    <span>Galerie</span>
                    {publicContent || privateContent}
                </h2>
                {noImages ? (
                    <>
                        <div
                            className={classNames([
                                sectionCss.galleryRoot,
                                sectionCss.placeholderWrapper,
                                css.placeholderWrapper,
                            ])}
                        >
                            <div className={sectionCss.firstImageColumn} />
                            <div
                                className={`${sectionCss.secondColumnImages} ${sectionCss.placeholderSecondRow}`}
                            >
                                <div className={sectionCss.imagePlaceholder} />
                                <div className={sectionCss.imagePlaceholder} />
                                <div className={sectionCss.imagePlaceholder} />
                            </div>
                        </div>

                        <NamedLink
                            name="EditUserInfo"
                            params={{ section: GALLERY_FORM, userType: userType || 'rider' }}
                            className={sectionCss.link}
                        >
                            <Button type="button" className={`${css.actionItem} ${css.actionBtn}`}>
                                <FormattedMessage id="ProfileSettingsForm.editUserInfoAction-userVideosImages" />
                            </Button>
                        </NamedLink>
                    </>
                ) : (
                    <div className={classes}>
                        <div className={sectionCss.galleryRoot}>
                            {isMobile ? (
                                <>
                                    {firstImage}
                                    {secondColumnImages}
                                    {restImagesColumn}
                                </>
                            ) : (
                                <>
                                    <div className={sectionCss.firstImageColumn}>{firstImage}</div>
                                    <div className={sectionCss.secondColumnImages}>
                                        {secondColumnImages}
                                    </div>
                                    <div className={sectionCss.restImagesColumn}>
                                        {restImagesColumn}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
            {imageCarouselOpen && (
                <ModalPortal
                    id="GallerySection.imageCarousel"
                    scrollLayerClassName={sectionCss.carouselModalScrollLayer}
                    containerClassName={sectionCss.carouselModalContainer}
                    contentClassName={sectionCss.carouselModalContent}
                    containerClassNameJoined
                    isOpen={imageCarouselOpen}
                    onClose={() => setImageCarouselOpen(false)}
                >
                    {galleryImages.map((image, index) => (
                        <ThumbnailWrapper
                            image={image}
                            index={index}
                            key={image.id?.uuid || image.id}
                            allImages={galleryImages || []}
                            showCarouselClassName={sectionCss.showCarouselNonFilled}
                            className={sectionCss.carouselThumbnail}
                            showImageCarousel
                            {...commonProps}
                        />
                    ))}
                </ModalPortal>
            )}
        </>
    );
};

export default compose(injectIntl)(GallerySection);
